import { computed, type Ref, toValue } from 'vue';
import { useQuery, type UseQueryReturnType } from '@tanstack/vue-query';
import type { AccountingInfo } from '@/types/deepadmin/AccountingInfo';
import { useApi } from '@/composables/useApi.ts';

interface UseAccountingInfoQueryParams {
  companyId: Ref<string | null | undefined>;
  enabled: Ref<boolean>;
}

export function useOrganizationAccountingQuery(
  params: UseAccountingInfoQueryParams
): UseQueryReturnType<AccountingInfo, Error> {
  const { deepAdminAPI } = useApi();
  const queryKey = computed(
    () => ['organization', toValue(params.companyId), 'accountingInfo'] as const
  );

  const query = useQuery<AccountingInfo, Error>({
    queryKey,
    enabled: params.enabled,
    queryFn: async ({ signal }) => {
      const id = toValue(params.companyId);
      if (!id) throw new Error('Missing company id');
      const first = await deepAdminAPI.value.usersCompanyAccounting.get(
        id,
        { refresh: false },
        { signal }
      );
      const data = first?.data as AccountingInfo | undefined;
      if (!data) throw new Error('No accounting data');
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  return query;
}
